import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Booking, MergedGroup } from '../booking';
import ViewMergedBookingsModal from './ViewMergedBookingsModal';
import ManualMerge from './ManualMerge';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showCancelConfirmation, showUnmergeConfirmation, showError } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

interface MergedBookingsModalProps {
  show: boolean;
  onHide: () => void;
  mergedDetails: MergedGroup[];
  mergedModalError: string | null;
  statusColors: { [key: string]: string };
  mergedFilter: string;
  setMergedFilter: (filter: string) => void;
  fetchMergedDetails: (statusFilter: string) => void;
}

const currentUser = {
  empNumber: localStorage.getItem('username') || localStorage.getItem('empNumber') || 'unknown',
  roles: JSON.parse(localStorage.getItem('roles') || '[]'),
  group: localStorage.getItem('group') || null,
};

const canCancelMergedGroup = (group: MergedGroup): boolean => {
  const isMerged = group.bookings.every((booking) => booking.status?.toUpperCase() === 'MERGED');
  return isMerged;
};

const canUnmergeGroup = (group: MergedGroup): boolean => {
  const isMerged = group.bookings.every((booking) => booking.status?.toUpperCase() === 'MERGED');
  return isMerged;
};

const MergedBookingsModal: React.FC<MergedBookingsModalProps> = ({
  show,
  onHide,
  mergedDetails,
  mergedModalError,
  statusColors,
  mergedFilter,
  setMergedFilter,
  fetchMergedDetails,
}) => {
  const [selectedGroup, setSelectedGroup] = useState<MergedGroup | null>(null);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualMergeModal, setShowManualMergeModal] = useState<boolean>(false);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilter = e.target.value;
    setMergedFilter(newFilter);
    fetchMergedDetails(newFilter);
  };

  const handleViewGroupClick = (group: MergedGroup) => {
  setSelectedGroup(group);
  setSelectedGroupId(group.groupId);
  setShowViewModal(true);
};


  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedGroup(null);
  };

  const handleCancelClick = (groupId: string) => {
    setSelectedGroupId(groupId);
    setShowCancelModal(true);
    setCancelReason('');
  };

  const handleUnmergeClick = (groupId: string) => {
    setSelectedGroupId(groupId);
    showUnmergeConfirmation(async () => {
      try {
        setLoading(true);
        await apiDefinitions.unmergeVehicleBookings(groupId);
        setError(null);
        fetchMergedDetails(mergedFilter);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.statusDescription || 'Failed to unmerge group: ' + (err.message || 'Unknown error');
        if (err.response?.status === 401) {
          showError('You are not authorized to unmerge this group. Please check your permissions.');
          setError('You are not authorized to unmerge this group. Please check your permissions.');
        } else if (err.response?.status === 404) {
          showError('Merged group not found. It may have been deleted or does not exist.');
          setError('Merged group not found. It may have been deleted or does not exist.');
        } else {
          showError(errorMessage);
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
        setSelectedGroupId(null);
      }
    });
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) {
      showError('Please provide a reason for cancellation.');
      setError('Please provide a reason for cancellation.');
      return;
    }
    if (!selectedGroupId) {
      showError('No merged group selected for cancellation.');
      setError('No merged group selected for cancellation.');
      return;
    }

    setLoading(true);
    showCancelConfirmation(async () => {
      try {
        await apiDefinitions.cancelVehicleBookingRequest(cancelReason, null, selectedGroupId);
        setShowCancelModal(false);
        setError(null);
        fetchMergedDetails(mergedFilter);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.statusDescription || 'Failed to cancel merged group: ' + (err.message || 'Unknown error');
        if (err.response?.status === 401) {
          showError('You are not authorized to cancel this merged group. Please check your permissions.');
          setError('You are not authorized to cancel this merged group. Please check your permissions.');
        } else if (err.response?.status === 404) {
          showError('Merged group not found. It may have been deleted or does not exist.');
          setError('Merged group not found. It may have been deleted or does not exist.');
        } else {
          showError(errorMessage);
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    });
  };

  const handleManualMergeClick = () => {
    setShowManualMergeModal(true);
  };

  const handleCloseManualMergeModal = () => {
    setShowManualMergeModal(false);
  };

  const handleUpdateSuccess = () => {
    fetchMergedDetails(mergedFilter);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title className="mb-0">Merged Bookings</Modal.Title>
            <div className="d-flex align-items-center gap-3">
              <Form.Group className="mb-0 d-flex align-items-center">
                <Form.Label className="mb-0 me-2 fw-medium">Filter by Status</Form.Label>
                <Form.Select
                  value={mergedFilter}
                  onChange={handleFilterChange}
                  size="sm"
                  style={{ minWidth: 140 }}
                >
                  <option value="ALL">All</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="PENDING">Pending</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="REJECTED">Rejected</option>
                </Form.Select>
              </Form.Group>
              <Button
                variant="success"
                size="sm"
                onClick={handleManualMergeClick}
                title="Create Manual Merge"
              >
                Manual Merge
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {mergedModalError && <div className="alert alert-danger">{mergedModalError}</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!mergedModalError && mergedDetails.length === 0 && (
            <div className="alert alert-info">No merged bookings available.</div>
          )}
          {!mergedModalError && mergedDetails.length > 0 && (
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>Group ID</th>
                    <th>Booking IDs</th>
                    <th>Suggested Departure Time</th>
                    <th>Suggested Vehicle</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedDetails.map((group, index) => (
                    <tr key={index}>
                      <td>{group.groupId}</td>
                      <td>
                        {group.bookings.map((booking) => (
                          <span key={booking.id} className={`badge ${statusColors[booking.status] || 'bg-secondary'} me-1`}>
                            {booking.id} ({booking.status})
                          </span>
                        ))}
                      </td>
                      <td>
                        {group.suggestedDepartureTime
                          ? new Date(Number(group.suggestedDepartureTime)).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td>
                      {group.vehicleSuggestions && group.vehicleSuggestions.length > 0 ? (
                        group.vehicleSuggestions.map((vehicle, idx) => (
                          <span key={idx} className="badge bg-success me-1">
                            {vehicle.numberPlate}
                          </span>
                        ))
                      ) : (
                        'N/A'
                      )}
                    </td>
                      <td>
                        <div className="hstack gap-2 fs-3">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="btn-icon rounded-circle"
                            onClick={() => handleViewGroupClick(group)}
                            title={`View Group ${group.groupId}`}
                          >
                            <i className="feather feather-eye" />
                          </Button>
                          {canCancelMergedGroup(group) && (
                            <Button
                              variant="danger"
                              size="sm"
                              className="btn-icon rounded-circle"
                              onClick={() => handleCancelClick(group.groupId)}
                              title={`Cancel Group ${group.groupId}`}
                            >
                              <i className="feather feather-x" />
                            </Button>
                          )}
                          {canUnmergeGroup(group) && (
                            <Button
                              variant="info"
                              size="sm"
                              className="btn-icon rounded-circle"
                              onClick={() => handleUnmergeClick(group.groupId)}
                              title={`Unmerge Group ${group.groupId}`}
                            >
                              <i className="fas fa-link" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {selectedGroupId && (
    <ViewMergedBookingsModal
      show={showViewModal}
      onHide={handleCloseViewModal}
      groupId={selectedGroupId}
      statusColors={statusColors}
      onUpdateSuccess={handleUpdateSuccess}
    />
  )}
      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        aria-labelledby="cancel-merged-group-modal"
        aria-modal="true"
        autoFocus={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title id="cancel-merged-group-modal">Cancel Merged Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason for Cancellation</Form.Label>
            <Form.Control
              type="text"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation"
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowCancelModal(false)}>
            Close
          </Button>
          <Button
            variant="secondary"
            onClick={handleCancelSubmit}
            disabled={loading || !cancelReason.trim()}
          >
            Confirm Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      <ManualMerge
        show={showManualMergeModal}
        onHide={handleCloseManualMergeModal}
        statusColors={statusColors}
      />
    </>
  );
};

export default MergedBookingsModal;